import { Link, useNavigate } from 'react-router-dom';
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import config from '../config/config';
import Logo from '../../src/assets/images/ALKHIDMAT-Logo.png';
import Logo1 from '../../src/assets/images/Iq Logo.png';



const formSchema = z.object({
  email: z.string().min(5, { message: "Please enter a valid email or phone number." }),
  password: z.string().min(6, { message: "Password must be at least 6 characters." }),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export function RegisterForm() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [error, setError] = useState(null);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  async function onSubmit(values) {
    setIsLoading(true);
    setError(null);
    try {
      const { confirmPassword, ...registerData } = values;
      registerData.role = 'user';

      const response = await fetch(`${config.baseUrl}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(registerData),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Registration failed');

      setRegistrationSuccess(true);
      setTimeout(() => {
        navigate("/", { replace: true });
      }, 3000);

    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }

  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-[#F1F5F9] flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full mx-auto bg-white p-10 rounded-3xl shadow-xl text-center border border-slate-200">
          <div className="mx-auto h-20 w-20 bg-blue-50 rounded-full flex items-center justify-center mb-6">
            <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h2 className="text-3xl font-black text-slate-900 mb-3">Welcome to Zabiha!</h2>
          <p className="text-slate-500 font-medium">Your account has been created successfully.</p>
          <div className="mt-8 flex justify-center">
            <div className="animate-bounce bg-blue-600 p-2 w-10 h-10 ring-8 ring-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
          </div>
          <p className="mt-6 text-xs text-slate-400 uppercase tracking-widest font-bold">Redirecting to Login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center lg:bg-zinc-100 dark:bg-zinc-900">
      <div className="max-w-md w-full mx-auto space-y-8 bg-white p-10 rounded-3xl shadow-xl border border-slate-200">

        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-38 w-68 items-center justify-center  shadow-md shadow-blue-100 border-4 border-white bg-sky-700 overflow-hidden ">
            <img src={Logo1} alt="header logo" className="h-full w-full object-cover" />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex p-1.5 bg-slate-200 rounded-xl border border-slate-200">
          <Link to="/" className="flex-1 py-2.5 text-sm font-semibold  text-slate-500 hover:text-blue-600 text-center transition-all">
            Login
          </Link>
          <button className="flex-1 py-2.5 text-sm font-bold rounded-xl bg-white text-blue-600 shadow-sm border-none">
            Register
          </button>
        </div>

        <form onSubmit={form.handleSubmit(onSubmit)} className="mt-8 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs font-bold rounded">
              {error}
            </div>
          )}

          <div className="space-y-2">
            {/* Email Field */}
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase mb-2 ml-1">Email Address</label>
              <input
                {...form.register("email")}
                type="text"
                className={`block w-full px-4 py-2 border rounded-md bg-slate-50 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 sm:text-sm transition-all ${form.formState.errors.email ? 'border-red-300' : 'border-slate-200'
                  }`}
                placeholder="Enter your email or Phone No."
              />
              {form.formState.errors.email && (
                <p className="mt-1.5 text-[11px] text-red-500 font-bold ml-2">{form.formState.errors.email.message}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase mb-2 ml-1">Create Password</label>
              <input
                {...form.register("password")}
                type="password"
                className={`block w-full px-4 py-2 border rounded-md bg-slate-50 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 sm:text-sm transition-all ${form.formState.errors.password ? 'border-red-300' : 'border-slate-200'
                  }`}
                placeholder="Min. 6 characters"
              />
              {form.formState.errors.password && (
                <p className="mt-1.5 text-[11px] text-red-500 font-bold ml-2">{form.formState.errors.password.message}</p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase mb-2 ml-1">Confirm Password</label>
              <input
                {...form.register("confirmPassword")}
                type="password"
                className={`block w-full px-4 py-2 border rounded-md bg-slate-50 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500 sm:text-sm transition-all ${form.formState.errors.confirmPassword ? 'border-red-300' : 'border-slate-200'
                  }`}
                placeholder="Repeat your password"
              />
              {form.formState.errors.confirmPassword && (
                <p className="mt-1.5 text-[11px] text-red-500 font-bold ml-2">{form.formState.errors.confirmPassword.message}</p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2.5 text-sm sm:text-base font-bold rounded-md text-white bg-sky-700 hover:bg-blue-900"
          >
            {isLoading ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating Account...
              </span>
            ) : (
              "REGISTER NOW"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}